import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormControl } from '@angular/forms';
import { NzModalService } from 'ng-zorro-antd/modal';
import { DeoCreateNewProjectComponent, ProjectModalMode, ProjectModalResult } from './deo-create-new-project/deo-create-new-project.component';
import { EllipsisMenuItem } from '@gainsystems/gains-ui-commons';
import { DeoDeleteProjectComponent, DeleteProjectModalResult } from './deo-delete-project/deo-delete-project.component';
import { GainsToastService } from 'app/shared/services/gains-toast-service/gains-toast.service';
import { AIAgentService } from '../../services/ai-agent.service';
import { DeoOwnershipService } from '../../services/deo-ownership.service';
import { getApiErrorMessage, getResponseErrorMessage } from '../../services/api-error.helper';
import { CreateProjectRequest, DEOProject, EditProjectRequest, ProjectsDeletedEvent } from '../../models/chat.models';
import { SelectOption } from '@gainsystems/gains-ui-commons';
import { Router } from '@angular/router';
import { UiStateStorageService } from 'app/shared/services/ui-state-storage/ui-state-storage.service';
import { forkJoin } from 'rxjs';
import { formatRelativeTimeLabel } from 'app/shared/util/relative-time.util';

const LOAD_FAILED_MESSAGE = 'Failed to load projects. Please try again.';

// Shown instead of a relative label when the backend sent no usable updated_at; matches the
// project folder view's header, so the same project never reads as fresher on one screen.
const UNKNOWN_UPDATED_LABEL = '--';

interface Project {
  id: number | string;
  name: string;
  description: string;
  access: string;
  color: string;
  chats: number;
  artifacts: number;
  starred: boolean;
  updatedLabel: string;
  updatedAtMs: number;
  checked: boolean;
  /** False for a project another user shared with "Everyone": it can be opened, never edited or deleted. */
  isMine: boolean;
}

@Component({
  standalone: false,
  selector: 'deo-projects',
  templateUrl: './deo-projects.component.html',
  styleUrls: ['./deo-projects.component.scss']
})
export class DeoProjectsComponent implements OnInit {
  private static readonly _deoAiAssistantStateKey = 'deo-ai-assistant';

  @Output() loadingChange = new EventEmitter<boolean>();
  @Output() projectsDeleted$ = new EventEmitter<ProjectsDeletedEvent>();

  searchQuery = '';
  modalRef: any;

  sortOptions = [
    { title: 'Recent Activity', value: '2' },
    { title: 'Alphabetical', value: '1' },
    { title: 'Date Created', value: '3' },
    { title: 'Project Order', value: '4' }
  ];
  sortControl = new FormControl<SelectOption | null>(this.sortOptions[0]);
  selectedSortBy = 2;

  projectMenuItems: EllipsisMenuItem[] = [{ title: 'Edit' }, { title: 'Delete', warning: true }];

  projects: Project[] = [];
  selectedProjectIds: (number | string)[] = [];

  isLoading = false;
  /** Backend failure message for the last load, or null when it succeeded. Drives the inline error state. */
  loadError: string | null = null;

  constructor(
    private readonly _modalService: NzModalService,
    private readonly _toastService: GainsToastService,
    private readonly _aiAgentService: AIAgentService,
    private readonly _router: Router,
    private readonly _uiStateStorageService: UiStateStorageService,
    private readonly _ownershipService: DeoOwnershipService
  ) {}

  get filteredProjects(): Project[] {
    const q = this.searchQuery.toLowerCase().trim();
    const filtered = q ? this.projects.filter(p => p.name.toLowerCase().includes(q)) : this.projects;
    return this.sortProjects(filtered);
  }

  setFavouriteProject(project: Project): void {
    const previousStarred = project.starred;
    this.applyFavorite(project, !previousStarred);

    // Temp projects are not persisted yet, so just keep the toggle local.
    if (this.isTempProject(project.id)) {
      return;
    }

    this._aiAgentService.editProject({ project_id: String(project.id), is_favorite: project.starred }).subscribe({
      next: response => {
        if (!response?.succeeded) {
          this.handleFavoriteFailure(project, previousStarred);
        }
      },
      error: error => {
        this.handleFavoriteFailure(project, previousStarred, getApiErrorMessage(error, 'Failed to update favorite. Please try again.'));
      }
    });
  }

  private applyFavorite(project: Project, starred: boolean): void {
    project.starred = starred;
    // Force change detection to trigger filteredProjects getter and re-sort
    this.projects = [...this.projects];
  }

  private handleFavoriteFailure(project: Project, previousStarred: boolean, message = 'Failed to update favorite. Please try again.'): void {
    this.applyFavorite(project, previousStarred);
    this._toastService.error('Error', message);
  }

  onCardControlClick(event: Event): void {
    event.stopPropagation();
  }

  isProjectSelected(projectId: number | string): boolean {
    return this.selectedProjectIds.includes(projectId);
  }

  /** Space would otherwise toggle the input natively, which fights the [checked] binding. */
  onCheckboxKey(event: Event, projectId: number | string): void {
    event.preventDefault();
    this.toggleProjectSelection(projectId, event);
  }

  toggleProjectSelection(projectId: number | string, event: Event): void {
    event?.stopPropagation();
    const index = this.selectedProjectIds.indexOf(projectId);
    if (index > -1) {
      this.selectedProjectIds.splice(index, 1);
    } else {
      this.selectedProjectIds.push(projectId);
    }
  }

  deleteSelectedProjects(event: any): void {
    const idsToDelete = [...this.selectedProjectIds];
    if (idsToDelete.length === 0) {
      return;
    } else {
      this.openDeleteConfirmationModal(this.selectedProjectIds.map(id => this.projects.find(p => p.id === id)).filter(p => p) as Project[]);
    }
  }

  trackByProjectId(_: number, project: Project): number | string {
    return project.id;
  }

  onFavoriteClick(event: Event, project: Project): void {
    event.stopPropagation();
    if (!project.isMine) {
      return;
    }
    this.setFavouriteProject(project);
  }

  selectProject(project: Project): void {
    this._uiStateStorageService.saveCustomState(DeoProjectsComponent._deoAiAssistantStateKey, {
      activeTab: 'projects'
    });

    this.setProjectBreadcrumb(project.name);

    // Navigate to project folder view with project id as route parameter
    this._router.navigate(['/deo-ai-assist/project'], { queryParams: { projectId: project.id } });
  }

  private setProjectBreadcrumb(projectName: string): void {
    const featureRoute = this._router.config.find(route => route.path === 'deo-ai-assist') as any;
    const childRoutes = featureRoute?._loadedRoutes ?? featureRoute?.children ?? [];
    const projectRoute = childRoutes.find((route: any) => route.path === 'project');

    if (!projectRoute) {
      return;
    }

    projectRoute.data = {
      ...(projectRoute.data ?? {}),
      breadcrumb: projectName
    };
  }

  getProjectIconColor(project: Project): string {
    const color = (project.color ?? '').trim();
    return color;
  }

  onProjectMenuAction(action: string, project: Project): void {
    if (!project.isMine) {
      return;
    }

    if (action === 'Edit') {
      this.openProjectModal('edit', project);
      return;
    }

    if (action === 'Delete') {
      this.openDeleteConfirmationModal([project]);
    }
  }

  openDeleteConfirmationModal(projects: Project[]): void {
    this.modalRef = this._modalService.create({
      nzContent: DeoDeleteProjectComponent,
      nzFooter: null,
      nzClosable: true,
      nzCentered: true,
      nzWidth: 540,
      nzOnCancel: () => this.modalRef.close(),
      nzData: {
        isSingleProject: projects.length === 1,
        projects: projects
      },
      nzBodyStyle: {
        padding: '0'
      }
    });

    this.modalRef.afterClose.subscribe((result: DeleteProjectModalResult | undefined) => {
      if (!result?.confirmed) {
        return;
      }

      this.deleteProject(projects, result.deleteChats);
    });
  }

  openProjectModal(mode: ProjectModalMode = 'create', projectToEdit?: Project): void {
    this.modalRef = this._modalService.create({
      nzContent: DeoCreateNewProjectComponent,
      nzFooter: null,
      nzClosable: true,
      nzCentered: true,
      nzWidth: 540,
      nzOnCancel: () => this.modalRef.close(),
      nzData:
        mode === 'edit' && projectToEdit
          ? {
              mode,
              project: {
                name: projectToEdit.name,
                description: projectToEdit.description,
                access: projectToEdit.access,
                color: projectToEdit.color
              }
            }
          : { mode },
      nzBodyStyle: {
        padding: '0'
      }
    });

    this.modalRef.afterClose.subscribe((result: ProjectModalResult | undefined) => {
      if (!result) {
        return;
      }

      if (mode === 'edit' && projectToEdit) {
        this.updateProject(projectToEdit, result);
        return;
      }

      this.createProject(result);
    });
  }

  createProject(formData: ProjectModalResult): void {
    if (!formData.name.trim()) {
      return;
    }

    const tempProjectId = `temp-${Date.now()}`;
    const newProject: Project = {
      id: tempProjectId,
      name: formData.name,
      description: formData.description,
      access: formData.access,
      color: formData.color,
      chats: 0,
      artifacts: 0,
      starred: false,
      updatedLabel: 'just now',
      updatedAtMs: Date.now(),
      checked: false,
      isMine: true
    };

    this.projects.unshift(newProject);

    const payload: CreateProjectRequest = {
      name: formData.name,
      description: formData.description,
      visibility: this.mapAccessToVisibility(formData.access),
      color: formData.color
    };

    this._aiAgentService.createProject(payload).subscribe({
      next: response => {
        if (!response?.succeeded || !response.data) {
          this.handleCreateProjectFailure(tempProjectId);
          return;
        }

        const apiProject = this.mapApiProjectToUi(response.data);
        this.projects = this.projects.map(project => (project.id === tempProjectId ? apiProject : project));
        this._toastService.success('New Project', 'Project created successfully');
      },
      error: error => {
        this.handleCreateProjectFailure(tempProjectId, getApiErrorMessage(error, 'Failed to create project. Please try again.'));
      }
    });
  }

  private handleCreateProjectFailure(tempProjectId: string, message = 'Failed to create project. Please try again.'): void {
    this.projects = this.projects.filter(project => project.id !== tempProjectId);
    this._toastService.error('Error', message);
  }

  private updateProject(projectToEdit: Project, formData: ProjectModalResult): void {
    // Temp projects were never persisted, so just apply the edit locally.
    if (this.isTempProject(projectToEdit.id)) {
      this.applyProjectEdit(projectToEdit, formData);
      this._toastService.success('Project Updated', 'Project updated successfully');
      return;
    }

    const payload: EditProjectRequest = {
      project_id: String(projectToEdit.id),
      name: formData.name,
      description: formData.description,
      visibility: this.mapAccessToVisibility(formData.access),
      color: formData.color
    };

    this._aiAgentService.editProject(payload).subscribe({
      next: response => {
        if (!response?.succeeded) {
          this._toastService.error('Error', 'Failed to update project. Please try again.');
          return;
        }
        this.applyProjectEdit(projectToEdit, formData);
        this._toastService.success('Project Updated', 'Project updated successfully');
      },
      error: error => {
        this._toastService.error('Error', getApiErrorMessage(error, 'Failed to update project. Please try again.'));
      }
    });
  }

  private applyProjectEdit(projectToEdit: Project, formData: ProjectModalResult): void {
    projectToEdit.name = formData.name;
    projectToEdit.description = formData.description;
    projectToEdit.access = formData.access;
    projectToEdit.color = formData.color;
    projectToEdit.updatedLabel = 'just now';
    projectToEdit.updatedAtMs = Date.now();
    this.projects = [...this.projects];
  }

  private deleteProject(projects: Project[], deleteChats: boolean): void {
    if (projects.length === 0) {
      return;
    }

    const persistedProjects = projects.filter(project => !this.isTempProject(project.id));

    // Temp projects were never persisted, so when nothing reached the backend just drop them locally.
    if (persistedProjects.length === 0) {
      this.finalizeProjectDeletion(projects, deleteChats);
      return;
    }

    forkJoin(persistedProjects.map(project => this._aiAgentService.deleteProject(project.id, deleteChats))).subscribe({
      next: responses => {
        if (!responses.every(response => response.succeeded)) {
          this._toastService.error('Error', 'Failed to delete one or more projects. Please try again.');
          return;
        }
        this.finalizeProjectDeletion(projects, deleteChats);
      },
      error: error => {
        this._toastService.error('Error', getApiErrorMessage(error, 'Failed to delete project. Please try again.'));
      }
    });
  }

  private finalizeProjectDeletion(projects: Project[], deleteChats: boolean): void {
    const projectIds = projects.map(project => project.id);
    this.projects = this.projects.filter(project => !projectIds.includes(project.id));
    this.selectedProjectIds = this.selectedProjectIds.filter(id => !projectIds.includes(id));
    this.notifyProjectsDeleted(projects.length);
    this.projectsDeleted$.emit({ projectIds, deletedChats: deleteChats });
  }

  private notifyProjectsDeleted(deletedCount: number): void {
    const title = deletedCount === 1 ? 'Project Deleted' : 'Projects Deleted';
    const message = deletedCount === 1 ? 'Project deleted successfully' : 'The selected projects have been deleted.';
    this._toastService.success(title, message);
  }

  private isTempProject(projectId: number | string): boolean {
    return typeof projectId === 'string' && projectId.startsWith('temp-');
  }

  private mapApiProjectToUi(project: DEOProject): Project {
    return {
      id: project.id,
      name: project.name,
      description: project.description || '',
      access: this.mapVisibilityToAccess(project.visibility),
      color: project.color || '#9E9E9E',
      chats: project.chats_count ?? 0,
      artifacts: project.artifacts_count ?? 0,
      starred: project.is_favorite,
      updatedLabel: this.formatUpdatedLabel(project.updated_at),
      updatedAtMs: this.parseDateToTimestamp(project.updated_at),
      checked: false,
      isMine: this._ownershipService.isMine(project.user_id)
    };
  }

  private mapVisibilityToAccess(visibility: string): string {
    return visibility === 'Public' ? 'Everyone' : 'Only Me (Private)';
  }

  private mapAccessToVisibility(access: string): string {
    return access === 'Everyone' ? 'Public' : 'Private';
  }

  private formatUpdatedLabel(updatedDate: string): string {
    return formatRelativeTimeLabel(updatedDate) || UNKNOWN_UPDATED_LABEL;
  }

  private parseDateToTimestamp(dateValue: string): number {
    const parsed = new Date(dateValue).getTime();
    return Number.isNaN(parsed) ? 0 : parsed;
  }

  private sortProjects(projects: Project[]): Project[] {
    const sortedProjects = [...projects];
    let sorted: Project[];

    switch (this.selectedSortBy) {
      case 1:
        sorted = sortedProjects.sort((a, b) => a.name.localeCompare(b.name));
        break;
      case 3:
        sorted = sortedProjects.sort((a, b) => b.name.localeCompare(a.name));
        break;
      case 4:
        sorted = sortedProjects.sort((a, b) => Number(b.starred) - Number(a.starred));
        break;
      case 2:
      default:
        sorted = sortedProjects.sort((a, b) => b.updatedAtMs - a.updatedAtMs);
    }

    // Always prioritize starred projects first
    return sorted.sort((a, b) => Number(b.starred) - Number(a.starred));
  }

  /** Re-runs the load after a failure, from the inline error state's Try again button. */
  retryLoad(): void {
    this.loadProjects();
  }

  private loadProjects(): void {
    this.isLoading = true;
    this.loadError = null;
    this.loadingChange.emit(true);
    this._aiAgentService.getProjects().subscribe({
      next: response => {
        if (response && !response.succeeded) {
          this.failLoading(getResponseErrorMessage(response, LOAD_FAILED_MESSAGE));
          return;
        }

        const apiProjects = response?.data ?? [];
        this.projects = this.sortProjects(apiProjects.map(project => this.mapApiProjectToUi(project)));
        this.finishLoading();
      },
      error: error => {
        this.failLoading(getApiErrorMessage(error, LOAD_FAILED_MESSAGE));
      }
    });
  }

  /**
   * Surfaces a failed load in the panel itself rather than as a toast: a toast auto-dismisses and leaves
   * the empty state claiming the user owns no projects, with nothing left to retry.
   */
  private failLoading(message: string): void {
    this.projects = [];
    this.loadError = message;
    this.finishLoading();
  }

  private finishLoading(): void {
    this.isLoading = false;
    this.loadingChange.emit(false);
  }

  private mapSortOptionToSortBy(optionValue: string | undefined): number {
    const parsed = parseInt(optionValue ?? '', 10);
    return Number.isNaN(parsed) ? 2 : parsed;
  }

  private bindSortControl(): void {
    this.sortControl.valueChanges.subscribe(selectedOption => {
      const optionValue = selectedOption?.value;
      this.selectedSortBy = this.mapSortOptionToSortBy(optionValue);
      this.loadProjects();
    });
  }

  ngOnInit(): void {
    this.bindSortControl();
    this.loadProjects();
  }
}
